/**
 * <TopProductsTable rows={hub.rows} seeAllHref="/sales/skus?..." onOpen={sku => setOpenSku(sku)} loading={loading} />
 *
 * Compact "Top products" table for the Sales overview: the 10 highest-revenue SKUs in the period
 * (zero-revenue rows hidden). Columns: product, revenue, ₹ change (DeltaBadge), units, avg price,
 * days of cover (coloured: < 15 red, < 30 amber). Sortable headers (aria-sort via SortHeader);
 * rows are keyboard-reachable (Tab + Enter/Space) and open the product drawer.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '../ui/skeleton';
import { SortHeader, useTableControls } from '../TableControls';
import { DeltaBadge } from './DeltaBadge';
import { coverTone, number, shortMoney, wholeMoney } from './format';

const COVER_STYLES = {
  danger: 'bg-red-50 text-red-700 ring-red-200',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200',
  success: 'bg-green-50 text-green-800 ring-green-200',
  neutral: 'bg-gray-100 text-gray-600 ring-gray-200',
};

export const CoverPill = ({ days }) => (
  <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums ring-1 ring-inset ${COVER_STYLES[coverTone(days)]}`}>
    {days == null ? '—' : `${number(Math.round(days))} d`}
  </span>
);

export const TopProductsTable = ({ rows = [], seeAllHref = '/sales/skus', onOpen, loading = false, limit = 10 }) => {
  const top = useMemo(() => (rows || []).filter(row => row.revenue > 0).sort((a, b) => b.revenue - a.revenue).slice(0, limit), [rows, limit]);
  const table = useTableControls(top, { initialSort: { key: 'revenue', dir: 'desc' }, pageSize: limit });
  const open = row => onOpen?.(row.key);
  return (
    <section className="min-w-0 rounded-xl border border-gray-200 bg-white shadow-sm" data-testid="sales-top-products" aria-busy={loading}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 pt-4">
        <h2 className="text-base font-semibold text-gray-900">Top products</h2>
        <Link to={seeAllHref} className="text-sm font-medium text-blue-700 hover:underline" data-testid="sales-see-all-products">See all products →</Link>
      </div>
      <div className={`relative mt-2 overflow-x-auto ${loading && top.length ? 'opacity-60' : ''}`}>
        <table className="w-full min-w-[640px] text-sm" aria-label="Top 10 products by revenue">
          <thead className="border-y border-gray-100 bg-gray-50 text-xs text-gray-600">
            <tr>
              <SortHeader label="Product" sortKey="product_name" table={table} className="pl-4" />
              <SortHeader label="Revenue" sortKey="revenue" table={table} align="right" />
              <SortHeader label="Change" sortKey="revenue_delta" table={table} align="right" />
              <SortHeader label="Units" sortKey="units" table={table} align="right" />
              <SortHeader label="Avg price" sortKey="avg_selling_price" table={table} align="right" />
              <SortHeader label="Cover" sortKey="days_of_cover" table={table} align="right" className="pr-4" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && !top.length && [0, 1, 2, 3, 4].map(i => (
              <tr key={`s${i}`}><td colSpan={6} className="px-4 py-2"><Skeleton className="h-6 w-full" /></td></tr>
            ))}
            {table.pageRows.map(row => (
              <tr key={row.key} tabIndex={0} onClick={() => open(row)} data-testid={`sales-top-row-${row.key}`}
                onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(row); } }}
                aria-label={`${row.product_name || row.key}: ${shortMoney(row.revenue)}. Open details`}
                className="cursor-pointer transition-colors hover:bg-blue-50/40 focus:bg-blue-50/60 focus:outline-none">
                <td className="max-w-[260px] py-2 pl-4 pr-3">
                  <span className="block truncate font-medium text-gray-900">{row.product_name || row.key}</span>
                  <span className="block truncate text-xs text-gray-500">{row.key}{row.brand ? ` · ${row.brand}` : ''}</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-900" title={wholeMoney(row.revenue)}>{shortMoney(row.revenue)}</td>
                <td className="px-3 py-2 text-right"><DeltaBadge current={row.revenue} previous={row.previous_revenue} size="xs" /></td>
                <td className="px-3 py-2 text-right tabular-nums">{number(row.units)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{row.avg_selling_price == null ? '—' : wholeMoney(row.avg_selling_price)}</td>
                <td className="py-2 pl-3 pr-4 text-right"><CoverPill days={row.days_of_cover} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && !top.length && (
          <p className="px-4 py-6 text-center text-sm text-gray-500" data-testid="sales-top-empty">No product sales in this period. Try a longer period or clear filters.</p>
        )}
      </div>
      <p className="px-4 pb-3 pt-2 text-[11px] text-gray-500">Cover = current stock ÷ average daily units in this period. Click a row for details.</p>
    </section>
  );
};

export const SalesPerformanceTable = TopProductsTable;
export default TopProductsTable;
